import DirectApi from '../../../directApi';
import {ExportInvoiceOnSuccess, ExportXeroInvoiceParams, InvoiceBillTo, XeroAccountItem} from './ExportInvoiceModal.type';
import Util from '../../../forecast-app/shared/util/util';
import {changedUnitPrices, toPreFlagAccountMap, toXeroAccountMap} from './ExportInvoiceModal.xeroLines';
import CreateXeroInvoiceMutation from '../../../mutations/CreateXeroInvoiceMutation';
import {showModal, MODAL_TYPE} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {BUTTON_STYLE, BUTTON_COLOR} from '../../../constants';
import {IntlShape} from 'react-intl';

const getXeroAccounts = async (projectBillTo: InvoiceBillTo | undefined): Promise<XeroAccountItem[]> => {
	const {billFrom} = projectBillTo || {};
	const {externalId} = billFrom || {};

	return DirectApi.Fetch_Raw(`xero/accounts/${externalId}`).then(async result => {
		if (result.status !== 403) {
			const json = await result.json();
			return json.map(account => ({
				value: account.AccountID,
				label: account.Name,
				taxRate: account.DefaultTaxRate,
				type: account.Type,
				code: account.Code,
			}));
		}

		return [];
	});
};

export const fetchXeroAccounts = async (
	projectBillTo: InvoiceBillTo | undefined,
	setAccounts: (accounts: XeroAccountItem[]) => void
): Promise<void> => {
	const accounts = await getXeroAccounts(projectBillTo);
	setAccounts(accounts);
};

const onExportXeroInvoiceSuccess = (
	result,
	intl: IntlShape,
	onSuccess: ExportInvoiceOnSuccess,
	onExportFailed: () => void
): void => {
	const {formatMessage} = intl;
	const {createXeroInvoice} = result;

	/*
	 * The modal closes only when the export SUCCEEDED. It used to close either way, because
	 * `onSuccess()` ran before this check - so a Xero refusal threw away every item, account and
	 * tracking pick the user had made, with no way back but making them all again.
	 */
	if (createXeroInvoice.success) {
		onSuccess();
		createToast({duration: 5000, message: formatMessage({id: 'integrations.xero.invoice_created'})});
	} else {
		onExportFailed();
		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'invoicing.not_exported',
			warningInformation: [createXeroInvoice.errorMessage || 'The invoice could not be exported.'],
			buttons: [
				{
					text: formatMessage({id: 'common.ok'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
			],
		});
	}
};

export const onExportXeroInvoice = ({
	intl,
	onSuccess,
	invoice,
	invoiceRows,
	billTo,
	projectInDescription,
	onError,
	useLinesTable,
	onExportFailed,
}: ExportXeroInvoiceParams): void => {
	const {billFrom} = billTo;

	/*
	 * Under the flag the line carries an item, a tax type and tracking as well as its account, and
	 * toXeroAccountMap omits each one that is absent. Off the flag this is byte-for-byte the map
	 * built before - AC1 - because that helper is not reached at all.
	 *
	 * Prices changed by picking a Product/Service ride along on the map. The xero service sends each
	 * as the line's UnitAmount and writes it back to the invoice entry only once Xero has ACCEPTED,
	 * so the browser makes ONE call with one outcome: a refused export writes nothing and leaves the
	 * modal open with the user's picks intact. Off the flag no price can have changed, so the map is
	 * unchanged.
	 */
	const accountMap = useLinesTable
		? toXeroAccountMap(invoiceRows, changedUnitPrices(invoiceRows))
		: toPreFlagAccountMap(invoiceRows);

	const input = {
		invoiceId: invoice.id,
		billToId: billTo.id,
		xeroClientId: billTo?.externalId,
		accountMap,
		tenantId: billFrom?.externalId,
		projectInDescription,
	};

	Util.CommitMutation(
		CreateXeroInvoiceMutation,
		input,
		result => onExportXeroInvoiceSuccess(result, intl, onSuccess, onExportFailed),
		true,
		null,
		onError
	);
};
